import { Alert, Button, Checkbox, Group, Modal, Stack, Text, TextInput } from '@mantine/core';
import { useForm } from '@mantine/form';
import { notifications } from '@mantine/notifications';
import { Decimal } from 'decimal.js';
import { useState } from 'react';
import type { ReactNode } from 'react';
import { z } from 'zod';
import { ApiUnreachableError } from '../api/client.js';
import { useCreateTrip } from '../api/hooks.js';
import { translating } from '../i18n/resolver.js';
import { useT } from '../i18n/useT.js';
import { queueTrip } from '../offline/enqueue.js';
import { ErrorMessage } from './ErrorMessage.js';

/**
 * Logging one trip.
 *
 * A component of its own rather than a part of the Kørebog page, because it is
 * opened from two places: the logbook, and the installed mobile app — which has
 * no sidebar and therefore no way to reach the logbook at all. The form has to
 * work where the person is, not send them somewhere else first.
 */

const tripSchema = z.object({
  date: z.string().min(1, 'trip.date'),
  startAddress: z.string().min(1, 'trip.from'),
  endAddress: z.string().min(1, 'trip.to'),
  /*
    A distance, as text.

    Not a NumberInput. A Danish keyboard produces "42,5" and a numeric keypad
    "42.5"; both are the same distance and both have to reach the API as
    "42.50". Parsing that is `parseDecimal`'s job, and a NumberInput would have
    made it the browser's, whose answer depends on the device locale.
  */
  distanceKm: z.string().min(1, 'trip.km'),
  purpose: z.string().min(1, 'trip.purpose'),
});

/** `"42,5"` or `"42.5"` → `"42.50"`, or null when it is not a distance. */
function toDistance(entered: string): string | null {
  const normalised = entered.trim().replace(',', '.');
  if (!/^\d{1,6}(\.\d{1,2})?$/u.test(normalised)) {
    return null;
  }
  const value = new Decimal(normalised);
  return value.greaterThan(0) ? value.toFixed(2) : null;
}

function today(): string {
  // The device's own day, not UTC: a trip logged at 23:30 in Copenhagen belongs
  // to that day, and `toISOString()` would file it as tomorrow for half an hour
  // every winter evening.
  const now = new Date();
  const local = new Date(now.getTime() - now.getTimezoneOffset() * 60_000);
  return local.toISOString().slice(0, 10);
}

export function LogTripModal({
  opened,
  onClose,
}: {
  readonly opened: boolean;
  readonly onClose: () => void;
}): ReactNode {
  const t = useT();
  const create = useCreateTrip();
  const [isPrivate, setPrivate] = useState(false);
  const [gps, setGps] = useState<'idle' | 'searching' | 'denied' | 'unavailable'>('idle');
  const [queued, setQueued] = useState(false);

  const form = useForm({
    mode: 'uncontrolled',
    initialValues: {
      date: today(),
      startAddress: '',
      endAddress: '',
      distanceKm: '',
      purpose: '',
    },
    validate: translating(tripSchema, t, 'trip'),
  });

  /**
   * Fill in the start address from the device's position.
   *
   * Coordinates, not a street name: turning a latitude into an address needs a
   * geocoding service, which is a third party seeing where the user is. The
   * coordinates are honest, they satisfy "where did the trip start", and they
   * can be typed over. Live route tracking is deliberately not done at all — it
   * needs background location permission, which is a great deal to ask in order
   * to save reading an odometer.
   */
  function useMyLocation(): void {
    if (navigator.geolocation === undefined) {
      setGps('unavailable');
      return;
    }
    setGps('searching');
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords;
        form.setFieldValue(
          'startAddress',
          `${latitude.toFixed(5)}, ${longitude.toFixed(5)}`,
        );
        setGps('idle');
      },
      (error) => {
        setGps(error.code === error.PERMISSION_DENIED ? 'denied' : 'unavailable');
      },
      { enableHighAccuracy: true, timeout: 10_000, maximumAge: 60_000 },
    );
  }

  async function submit(values: typeof form.values): Promise<void> {
    const distance = toDistance(values.distanceKm);
    if (distance === null) {
      form.setFieldError('distanceKm', t('trip.km'));
      return;
    }
    const payload = {
      date: values.date,
      startAddress: values.startAddress.trim(),
      endAddress: values.endAddress.trim(),
      distanceKm: distance,
      purpose: values.purpose.trim(),
      isBusiness: !isPrivate,
    };

    /*
      Offline is not an error here, it is the expected case.

      This form exists to be filled in beside a car, which is exactly where
      there is no signal. Queuing first and only then trying the network would
      be simpler; trying first is better, because when there *is* a connection
      the trip is priced and visible immediately rather than sitting in a local
      queue that looks like a failure.
    */
    if (!navigator.onLine) {
      await queueTrip(payload);
      setQueued(true);
      form.reset();
      return;
    }

    try {
      await create.mutateAsync(payload);
      notifications.show({ color: 'green', title: t('trip.saved'), message: '' });
      form.reset();
      setPrivate(false);
      onClose();
    } catch (error) {
      // A refusal from the server is shown and kept on screen. Only a request
      // that never reached it is queued: queuing something the API has already
      // said no to would retry it for ever.
      /*
        ApiUnreachableError, not TypeError. The browser's fetch throws a
        TypeError, but ApiClient catches it and rethrows this -- so a check
        for TypeError never matched, and a request that lost its
        connection mid-flight was reported as a failure instead of queued.
      */
      if (error instanceof ApiUnreachableError) {
        await queueTrip(payload);
        setQueued(true);
        form.reset();
        return;
      }
    }
  }

  return (
    <Modal opened={opened} onClose={onClose} title={t('trip.logTitle')} size="lg">
      <form noValidate onSubmit={form.onSubmit((values) => void submit(values))}>
        <Stack gap="sm">
          {queued && (
            <Alert color="blue" variant="light">
              {t('trip.queued')}
            </Alert>
          )}

          <TextInput
            type="date"
            label={t('trip.date')}
            withAsterisk
            key={form.key('date')}
            {...form.getInputProps('date')}
          />

          <TextInput
            label={t('trip.from')}
            withAsterisk
            key={form.key('startAddress')}
            {...form.getInputProps('startAddress')}
          />
          <Group gap="xs">
            <Button
              variant="subtle"
              size="compact-sm"
              onClick={useMyLocation}
              loading={gps === 'searching'}
            >
              {t('trip.useGps')}
            </Button>
            {gps === 'denied' && (
              <Text size="xs" c="dimmed">
                {t('trip.gpsDenied')}
              </Text>
            )}
            {gps === 'unavailable' && (
              <Text size="xs" c="dimmed">
                {t('trip.gpsUnavailable')}
              </Text>
            )}
          </Group>

          <TextInput
            label={t('trip.to')}
            withAsterisk
            key={form.key('endAddress')}
            {...form.getInputProps('endAddress')}
          />

          <TextInput
            label={t('trip.km')}
            withAsterisk
            inputMode="decimal"
            placeholder="42,5"
            key={form.key('distanceKm')}
            {...form.getInputProps('distanceKm')}
          />

          <TextInput
            label={t('trip.purpose')}
            description={t('trip.purposeHelp')}
            withAsterisk
            key={form.key('purpose')}
            {...form.getInputProps('purpose')}
          />

          <Checkbox
            label={t('trip.isPrivate')}
            description={t('trip.isPrivateHelp')}
            checked={isPrivate}
            onChange={(event) => {
              setPrivate(event.currentTarget.checked);
            }}
          />

          {create.isError && (
            <ErrorMessage error={create.error} title={t('trip.saveFailed')} />
          )}

          <Button type="submit" size="lg" loading={create.isPending}>
            {t('trip.save')}
          </Button>
        </Stack>
      </form>
    </Modal>
  );
}
